import { getLogger } from "@expert/logging";
import { Button, Divider, Menu, Text } from "@mantine/core";
import { withClickAnalytics } from "@soluto-private/eventualize-react";
import { type JSX, useMemo, useState } from "react";
import { CaretDown, CaretUp, CheckMark, CloseX, DoubleCheckMark } from "@/common-ui";
import { useCustomerProgram, useVerifiedCustomerStore } from "@/customer-info";
import { usePartnerConfig, usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { type CallbackState, useAgentSdk, useCallbackState, usePartner, useSessionId, type VoiceTask } from "@/sdk";
import type { SummaryResolution } from "@/session-summary/store/useSessionSummaryStore";
import { useSessionSummaryStore } from "@/session-summary/store/useSessionSummaryStore";
import { useAsyncLoading, useFeatureFlag } from "@/shared-utils";
import { handleEndCall } from "../../eventHandlers";
import { useControlsStore, withControlsLock } from "../controls.store";
import { selectProgramName, selectSurveyId } from "./survey-utils";

/* Conditions for transfer to survey:
    1. Does not have a callback scheduled
    2. Does not have an active conference
    3. Customer has not already left after conference
*/
const shouldTransferToSurvey = (
    voiceTask: VoiceTask,
    callbackState: CallbackState | null,
    isConference: boolean,
    conferencePanelOpen: boolean,
    resolutionStatus: SummaryResolution,
): boolean => {
    const hasCallback = !!callbackState;
    const activeConference = isConference && conferencePanelOpen;
    return !(
        hasCallback ||
        activeConference ||
        voiceTask.customerLeftTheConference ||
        resolutionStatus === "not resolved" ||
        resolutionStatus === "transferred"
    );
};

const shouldSendSurveyData = (
    voiceTask: VoiceTask,
    callbackState: CallbackState | null,
    isConference: boolean,
    conferencePanelOpen: boolean,
): boolean => {
    const hasCallback = !!callbackState;
    const activeConference = isConference && conferencePanelOpen;
    const customerLeft = voiceTask.customerLeftTheConference;

    return !hasCallback && !activeConference && !customerLeft;
};

const AnalyticButton = withClickAnalytics(Button<"button">, "Click");
const AnalyticMenuItem = withClickAnalytics(Menu.Item<"button">, "Click");
const logger = getLogger({ module: "EndCallButton" });
/**
 * Button used to hangup a call for everyone
 *
 * in certain situations a dropdown menu is shown to allow the agent to select a resolution
 */
export function EndCallButton({ task }: { task: VoiceTask }): JSX.Element | null {
    const agentSdk = useAgentSdk();
    const callbackState = useCallbackState();
    const { controlLock, isConference, conferencePanelOpen } = useControlsStore();
    const [opened, setOpened] = useState(false);
    const partner = usePartner();
    const { data: shouldHideUnresolvedDisposition } = useFeatureFlag("EWP-shouldHideUnresolvedDisposition", {
        partner,
    });

    const anyParticipantOnHold = useMemo(() => {
        const conferenceParticipantIsOnHold =
            task.conferenceParticipants.find((participant) => participant.type === "active" && participant.hold) !==
            undefined;
        const primaryParticipantIsOnHold = task.holdState.onHold;

        return conferenceParticipantIsOnHold || primaryParticipantIsOnHold;
    }, [task.conferenceParticipants, task.holdState]);

    const { survey, surveyType } = usePartnerConfig();
    const { surveyEnabled: partnerSurveyEnabled } = usePartnerFeatureConfigured();
    const useCustomerAgreementProgramName = survey?.useCustomerAgreementProgram ?? false;
    const excludedPrograms = survey?.excludedPrograms ?? [];

    const sessionId = useSessionId();
    const customerAgreementProgramName =
        useVerifiedCustomerStore().getVerifiedCustomer(sessionId)?.customer?.productName;
    const { customerProgram } = useCustomerProgram();

    const isClaimEligibleProgram = customerProgram?.claimEligible ?? false;
    const isPartnerClaimEligible = survey?.isClaimsEligible ?? false;
    const isClaimEligible = isClaimEligibleProgram || isPartnerClaimEligible;

    const isSurveyEligible = (resolutionStatus: SummaryResolution) =>
        partnerSurveyEnabled
            ? shouldTransferToSurvey(task, callbackState, isConference, conferencePanelOpen, resolutionStatus)
            : false;

    const { setResolved } = useSessionSummaryStore();

    const [isLoading, resolveAndEndCall] = useAsyncLoading(
        (
            resolutionStatus: SummaryResolution = "not resolved",
            hideResolutionStatusForm = false,
            claimFiled = false,
        ) => {
            return withControlsLock(async () => {
                if (resolutionStatus) setResolved(resolutionStatus, { hideResolutionStatusForm });

                if (
                    surveyType === "auto" &&
                    shouldSendSurveyData(task, callbackState, isConference, conferencePanelOpen)
                ) {
                    await agentSdk.sendToSurvey(task);
                    return;
                }

                const programName = selectProgramName(
                    useCustomerAgreementProgramName,
                    claimFiled,
                    customerProgram?.name,
                    customerAgreementProgramName,
                    survey?.ignoreProgram ?? false,
                    excludedPrograms,
                );

                const surveyId = selectSurveyId(useCustomerAgreementProgramName, claimFiled, customerProgram, survey);

                const shouldSendToSurvey =
                    isSurveyEligible(resolutionStatus) &&
                    (customerProgram || useCustomerAgreementProgramName || survey?.ignoreProgram) &&
                    programName &&
                    surveyId;

                if (shouldSendToSurvey) {
                    await agentSdk.sendToSurvey(task, { program: programName, surveyId });
                } else {
                    await handleEndCall(agentSdk, task);
                }
            })();
        },
    );

    const noAcceptedCall = !task.conferenceId || !task.conferenceStarted;

    if (noAcceptedCall) {
        // ? redundant check. consider removing and trusting the developer to use this component correctly
        logger.error(task, "EndCall button should never be displayed for a call that has not been accepted.");
    }

    /** Menu resolution shouldn't be shown when callbacks are scheduled or a conference is active */
    const showResolutionMenu = !callbackState && surveyType !== "auto";

    return showResolutionMenu ? (
        <Menu position="bottom-end" opened={opened} shadow="md" onChange={setOpened}>
            <Menu.Target>
                <AnalyticButton
                    analytics={{ actionId: "HangupButton_MenuDropdownToggle" }}
                    aria-label="End Call"
                    data-testid="call-controls-hangup-menu"
                    disabled={isLoading || task.status !== "assigned" || controlLock || anyParticipantOnHold}
                    loading={isLoading || controlLock}
                    radius="xl"
                    rightSection={
                        opened ? (
                            <CaretUp fill="var(--mantine-color-white)" />
                        ) : (
                            <CaretDown fill="var(--mantine-color-white)" />
                        )
                    }
                    size="xs"
                    color={opened ? "red.8" : "red.9"}
                >
                    <Text size="xs">End call</Text>
                </AnalyticButton>
            </Menu.Target>
            <Menu.Dropdown bd="none" bg="dark.7">
                <AnalyticMenuItem
                    analytics={{ actionId: "HangupButton_IssueResolved" }}
                    data-testid="call-controls-hangup-menu-resolved"
                    py="xs"
                    onClick={() => void resolveAndEndCall("resolved", true)}
                    leftSection={<CheckMark width={18} fill="var(--mantine-color-dark-2)" />}
                >
                    <Text size="xs">{!shouldHideUnresolvedDisposition ? "Issue resolved" : "End"}</Text>
                </AnalyticMenuItem>
                {isClaimEligible && (
                    <>
                        <AnalyticMenuItem
                            analytics={{ actionId: "HangupButton_IssueResolvedClaimFiled" }}
                            data-testid="call-controls-hangup-menu-claim-filed"
                            py="xs"
                            onClick={() => void resolveAndEndCall("resolved", true, true)}
                            leftSection={<DoubleCheckMark width={18} fill="var(--mantine-color-dark-2)" />}
                        >
                            <Text size="sm">Resolved, claim filed</Text>
                        </AnalyticMenuItem>
                        <Divider color="dark.4" mt={5} mb={5} />
                    </>
                )}

                {!shouldHideUnresolvedDisposition && (
                    <AnalyticMenuItem
                        analytics={{ actionId: "HangupButton_IssueNotResolved" }}
                        data-testid="call-controls-hangup-menu-not-resolved"
                        py="xs"
                        onClick={() => void resolveAndEndCall("not resolved", true)}
                        leftSection={<CloseX height={18} fill="var(--mantine-color-dark-2)" />}
                    >
                        <Text size="xs">Issue not resolved</Text>
                    </AnalyticMenuItem>
                )}
                <AnalyticMenuItem
                    analytics={{ actionId: "HangupButton_Transferred" }}
                    data-testid="call-controls-hangup-menu-transferred"
                    py="xs"
                    onClick={() => void resolveAndEndCall("transferred", true)}
                    leftSection={<CloseX height={18} fill="var(--mantine-color-dark-2)" />}
                >
                    <Text size="xs">Transferred</Text>
                </AnalyticMenuItem>
            </Menu.Dropdown>
        </Menu>
    ) : (
        <AnalyticButton
            analytics={{ actionId: "HangupButton" }}
            aria-label="End Call"
            data-testid="call-controls-hangup"
            disabled={isLoading || task.status !== "assigned" || controlLock || anyParticipantOnHold}
            loading={isLoading || controlLock}
            onClick={() => void resolveAndEndCall("not resolved")}
            radius="xl"
            size="xs"
            color="red.9"
        >
            <Text size="xs">End call</Text>
        </AnalyticButton>
    );
}
